/**
 * Deciding whether the store's "on a call" state is real.
 *
 * The SIP/WebRTC session lives in the page. A browser refresh destroys it, and
 * because NicePhone's originate is agent-first (Asterisk rings the advisor's
 * extension and bridges the customer), losing that leg tears the whole call
 * down. So after a refresh there is never a live call — but the store rehydrates
 * `callStatus: 'CALLING'` from localStorage, and the server keeps reporting the
 * call active for as long as its CallHistory row stays non-terminal (two hours,
 * or forever when the terminal webhook never correlates). The advisor was left
 * pinned to a call that no longer exists, unable to dial because originate
 * rejects with "You already have an active call in progress".
 *
 * The signal that makes this decidable is ownership, not elapsed time.
 * `ownedBySession` is set when THIS page instance starts a call and is
 * deliberately excluded from the persisted state, so after a reload it is false
 * while `callStatus` is still CALLING. That is an exact marker for "the call was
 * started by a page that no longer exists", with no timing race.
 *
 * Ownership alone is not enough to act on, because a second tab rehydrates the
 * same way while the first tab genuinely holds the call. So the widget must also
 * have finished initialising and report no live SIP session of its own. All four
 * conditions together mean: this page believes it is on a call, it did not start
 * that call, the phone is up, and the phone has no call.
 */

/**
 * @param {object}  state
 * @param {string}  state.callStatus      current store status
 * @param {boolean} state.ownedBySession  did THIS page instance start the call
 * @param {boolean} state.widgetReady     has the softphone finished initialising
 * @param {boolean} state.hasLiveSession  does the softphone hold a live SIP session
 * @param {string}  [state.callingStatus] the CALLING constant, injected to avoid a store import
 * @returns {boolean}
 */
export const isZombieCall = ({
  callStatus,
  ownedBySession,
  widgetReady,
  hasLiveSession,
  callingStatus = 'CALLING',
}) => {
  if (callStatus !== callingStatus) return false;   // not claiming to be on a call
  if (ownedBySession) return false;                 // this page started it — trust it
  if (!widgetReady) return false;                   // phone still coming up; too early to judge
  if (hasLiveSession) return false;                 // the phone really is on a call
  return true;
};

export default { isZombieCall };
